import { create } from 'd3';
import { VirtualGridTable, VirtualGridTableColumn } from "../VirtualGridTable/VirtualGridTable";
import { FixedDateEval, ICalificacionP, CalificacionEstablecerValoresPorDefecto, ObtenerResumenAsistenciasEnRango } from "../../pages/EventEvaluation";
import ic_done_selected from '/icons/ic_done_selected.svg?raw';
import { Tag } from '../Tag/Tag';
import { InputDate } from '../InputDate';
import { _DateToInputFmt } from '../../../utils/General';
import { CheckBox } from '../CheckBox';
import _L from '../../../utils/Labels';
import { Input } from '../Input';
import { ECalificacionTipoEval } from '../../../data/models/Entities';

interface IEvaluationTableOptions {
    beforeRender: (items: ICalificacionP[]) => void
}

export class EvaluationTable {
    private table: VirtualGridTable<ICalificacionP>;
    private fechasAsistencias: string[];
    private skeletonMode: boolean

    constructor(options: IEvaluationTableOptions) {
        this.fechasAsistencias = [];

        this.table = new VirtualGridTable({
            estimatedRowHeight: 60,
            getKey: (_, i) => i,
            buffer: 3,
            columns: this.GetColumns(),
            useRealRows: true,
            height: "100%",
            beforeRender: (items) => {
                // document.body.querySelectorAll(".litepicker").forEach(el => el.remove());
                options.beforeRender(items);
            },
            onRenderRow: (row, item) => {
                row.onclick = () => console.debug(item.Id, item);
            }
        });
    }

    public get _Element(): HTMLElement {
        return this.table.element;
    }

    public _SetItems(items: ICalificacionP[]): void {
        this.table.setItems(items);
    }

    public _SetFechasAsistencias(fechasAsistencias: string[]): void {
        this.fechasAsistencias = fechasAsistencias;
    }

    public _SkeletonMode(active: boolean): void {
        this.skeletonMode = active;
        const skeletonItems: any[] = active ? [{}, {}, {}, {}, {}, {}] : []
        this.table.setItems(skeletonItems);
    }

    public _Distroy(): void {
        document.body.querySelectorAll(".litepicker").forEach(el => el.remove());
    }

    public get _TableControl(): Pick<VirtualGridTable<ICalificacionP>, "scrollToItemIndex"> {
        return this.table;
    }

    private WrapperRenderCell(item: ICalificacionP, callback: (item: ICalificacionP) => string | HTMLElement) {
        if (this.skeletonMode) {
            const skeletonMode = document.createElement("div");
            skeletonMode.className = "animated-background";
            skeletonMode.style.height = "50px";
            return skeletonMode;
        }
        return callback(item);
    }

    private GetColumns(): VirtualGridTableColumn<ICalificacionP>[] {
        return [
            {
                key: "select" as any,
                header: "",
                width: "35px",
                renderCell: d => this.WrapperRenderCell(d, (d) => create("div")
                    .attr("class", "select-indicator-cell")
                    .call((cont) => {
                        const limpiarCalificacion = () => {
                            CalificacionEstablecerValoresPorDefecto(d);
                            this.table.refresh();
                        }
                        switch (d._Status) {
                            case "eval":
                                cont.classed("eval", true)
                                    .html(ic_done_selected)
                                    .on("click", limpiarCalificacion)
                                break;
                            case "no_apply":
                                cont.classed("no_apply", true)
                                    .on("click", limpiarCalificacion);
                                break;
                            case "eval_empty":
                                break;

                        }
                    }).node()
                ),
            },
            {
                key: "_FechaInicioFmt",
                header: _L("evaluacion.fecha_inicio"),
                width: "12%",
                minWidth: "80px",
                renderCell: (d) => this.WrapperRenderCell(d, d => d._FechaInicioFmt)
            },
            {
                key: "Materia",
                header: _L("evaluacion.materia"),
                width: "1.2fr",
                renderCell: d => this.WrapperRenderCell(d, (d) => {
                    const td = document.createElement("div")
                    td.className = "materia-cell " + (d._Status == "no_apply" ? "disabled" : "")
                    if (d.Tags?.length) {
                        td.innerHTML = d.Materia + "<br>"
                        td.classList.add("tags_container")
                        d.Tags.forEach((tagValue, i) => {
                            const tag = new Tag({
                                Value: tagValue,
                                HexColor: d.TagsColor ? d.TagsColor[i] : undefined,
                            })
                            td.append(tag._ControlContainer)
                        })
                    } else {
                        td.textContent = d.Materia
                    }
                    return td;
                })
            },
            {
                key: "Criterio",
                header: _L("evaluacion.criterio"),
                width: "1.3fr",
                renderCell: d => this.WrapperRenderCell(d, (d) => {
                    const td = document.createElement("div")
                    td.className = d._Status == "no_apply" ? "disabled" : ""
                    td.textContent = d.Criterio || _L("evaluacion.no_crit")
                    return td
                }),
            },
            {
                key: "_Calificacion",
                header: _L("evaluacion.eval"),
                width: "1fr",
                renderCell: d => this.WrapperRenderCell(d, (d) => {
                    const td = document.createElement("div")
                    td.className = "eval-cell"
                    switch (d.EvalTipo) {
                        case ECalificacionTipoEval.Numeros:
                            const inputNum = new Input(td, {
                                inputType: "number",
                                min: d.EvalNombre[0] || "",
                                max: d.EvalNombre[1] || "",
                                step: "0.01",
                                value: d._Calificacion,
                            });
                            inputNum.input.on("input", () => {
                                d._Calificacion = inputNum.value || "";
                                if (d._Calificacion && inputNum.Validate()) {
                                    d._Status = "eval";
                                } else {
                                    d._Status = "eval_empty";
                                }
                                // fnUpdateRow(tr, d, (d._Calificacion ? "eval" : "eval_empty"), inputFechaEval)
                            })
                            inputNum.input.on("blur", () => {
                                this.table.refresh();
                            })
                            inputNum.SetDefaultOptions([_L("general.no_apply")])
                                .OptionsOnStepItem((cont) => cont.html(`<i>${cont.datum()}</i>`))
                                .OptionsOnSelect(() => {
                                    d._Status = "no_apply";
                                    this.table.refresh();
                                })//fnUpdateRow(tr, d, "no_apply", inputFechaEval))
                            inputNum.Validate()
                            break;
                        default:
                            const esTipoColor = d.EvalTipo == ECalificacionTipoEval.Colores;
                            const esTipoCualitativo = d.EvalTipo == ECalificacionTipoEval.Cualitativa;
                            const inputCSel = new Input(td, { disabled: true, inputType: (!esTipoColor ? "textarea" : "text") });
                            const input = inputCSel.input.node() as HTMLInputElement;
                            const evaluacionesFinal = d._EvaluacionesConfig.concat({
                                Nombre: null,
                                Descripcion: _L("general.no_apply")
                            })
                            inputCSel.SetDefaultOptions(evaluacionesFinal)
                                .OptionsOnStepItem((item) => {
                                    const d = item.datum()
                                    if (d.Nombre === null) {
                                        item.html(`<i>${d.Descripcion}</i>`);
                                        return
                                    }
                                    if (esTipoColor) {
                                        item.append("div").attr("class", "circle").style("background-color", d.Nombre)
                                        item.append("span").text(d.Descripcion)
                                    }
                                    else if (esTipoCualitativo)
                                        item.html(d.Nombre);
                                    else
                                        item.html(`<b>${d.Nombre}</b>` + (d.Descripcion ? ": " + d.Descripcion : ""));
                                })
                                .OptionsMinWidth("120px")
                                .OptionsMaxWidth("230px")
                                .OptionsWidth("max-content")
                                .OptionsOnSetInputText((d) => d.Nombre)
                                .OptionsOnSelect((item) => {
                                    d._Calificacion = item.Nombre || "";
                                    d._Status = (item.Nombre === null) ? "no_apply" : (d._Calificacion) ? "eval" : "eval_empty";
                                    this.table.refresh();
                                    // fnUpdateRow(tr, d, evalStatus, inputFechaEval)
                                });

                            if (esTipoColor) {
                                input.type = "color";
                                input.value = d._Calificacion || "#ffffff";
                            } else {
                                input.value = d._Calificacion || "";
                            }
                            inputCSel.lblInput.node().onclick = (e) => {
                                if (e.target != inputCSel.lblInput.node() && e.target != input) return
                                e.stopPropagation();
                                if (inputCSel.OptionsListIsVisible())
                                    inputCSel.HideOptionsList();
                                else
                                    inputCSel.ShowOptionsList();
                            }
                            inputCSel.Validate()
                            break;
                    }
                    return td
                })
            },
            {
                key: "_FechaEval",
                header: _L("evaluacion.fecha_eval"),
                width: "0.9fr",
                minWidth: "90px",
                renderCell: d => this.WrapperRenderCell(d, (d) => {
                    const td = document.createElement("div")
                    td.className = "fecha-cell"
                    const inputDtEval = new InputDate(td, {
                        // highlightedDays: this.ObtenerResumenFechas().fechasEnRango,
                        lockDays: d._PickerData.lockDates,
                        format: "DD/MM/YY",
                        min: _DateToInputFmt(d._PickerData.min),
                        max: _DateToInputFmt(),
                        // value: FixedDateEval(d._FechaEval),
                        required: d._Status != "eval_empty",
                        onSelectDt: () => {
                            this.OnChangeFechaEval(d, inputDtEval)
                        },
                        pickerOptions: {
                            numberOfMonths: 2,
                            numberOfColumns: 3,
                            footer: (() => {
                                const template = create<HTMLDivElement>("div").attr("class", "evaluation-date-picker")
                                const cbx = new CheckBox()
                                    .SetLabel(_L("evaluacion.enable_only_period"))
                                    .SetChecked(d._PickerData.checkboxActive)
                                    .OnChange(() => {
                                        const isChecked = cbx.IsChecked()
                                        d._PickerData.checkboxActive = isChecked
                                        d._PickerData.min = new Date(d.DtInicio)
                                        d._PickerData.max = isChecked ? new Date(d.DtFin) : new Date()

                                        const resumenFechas = ObtenerResumenAsistenciasEnRango(d._PickerData.min, d._PickerData.max, this.fechasAsistencias)
                                        d._PickerData.lockDates = resumenFechas.lockDates
                                        d._PickerData.asistencias = resumenFechas.asistencias
                                        d._FechaEval = resumenFechas.asistencias.length ? new Date(resumenFechas.asistencias[resumenFechas.asistencias.length - 1]) : null

                                        const dt = FixedDateEval(d._FechaEval)

                                        console.warn("fix fecha selected", dt)

                                        inputDtEval.Picker.setLockDays(d._PickerData.lockDates)
                                        if (dt) {
                                            inputDtEval.Picker.setDate(dt)
                                            inputDtEval.Picker.gotoDate(dt)
                                        } else {
                                            inputDtEval.Picker.clearSelection()
                                        }

                                        console.warn(d._PickerData.asistencias)
                                        tagNoDates.classed("hide", !!d._PickerData.asistencias.length)
                                    })

                                template.append(() => cbx.Element.node())
                                const tagNoDates = template.append("div")
                                    .attr("class", "tag-no-found-dates " + (!!d._PickerData.asistencias.length ? "hide" : ""))
                                    .text(_L("evaluacion.no_found_dates"))
                                template.append("hr")
                                template.append("div").attr("class", "tag-info").text(_L("evaluacion.tag_solo_fechas_asistencia"))

                                return template.node()
                            })()
                        }
                    })
                    const dt = FixedDateEval(d._FechaEval)
                    if (dt) {
                        inputDtEval.Picker.setDate(dt)
                        inputDtEval.Picker.gotoDate(dt)
                    }
                    inputDtEval.Validate()

                    return td;
                })
            },
            {
                key: "_Observacion",
                header: _L("evaluacion.observacion"),
                width: "1fr",
                renderCell: d => this.WrapperRenderCell(d, (d) => {
                    const temp = document.createElement("template");
                    const txtObservacion = new Input(temp, { inputType: "textarea", value: d._Observacion }).input.node() as HTMLTextAreaElement
                    txtObservacion.oninput = () => {
                        d._Observacion = txtObservacion.value;
                    }
                    return txtObservacion
                })
            }
        ]
    }

    private OnChangeFechaEval(d: ICalificacionP, inputFechaEvalControl: InputDate) {
        const fechaEvalUTC = inputFechaEvalControl.ISOValueWithoutZH()
        if (!fechaEvalUTC) {
            d._FechaEval = null
            return
        }
        // const dtNow = new Date()
        const dtEval = new Date(fechaEvalUTC/* .toISOString().replace("Z", "") */)
        // if (dtNow.getFullYear() == dtEval.getFullYear() && dtNow.getMonth() == dtEval.getMonth() && dtNow.getDate() == dtEval.getDate()) {
        //     d._FechaEval = "today"
        // } else
        d._FechaEval = dtEval
    }
}